import { withDefaults } from '../../../core/theme';
import { seg, tween } from '../../../core/effect/motion';
import { parseZoneKey } from '../../../core/field/zones';
import {
  CARD_ASPECT, add, canvasOf, card, fade, fill, place,
} from './common';
import { OBJECT_STYLE } from '../drawObjects';
import { sourceOf } from './cardSource';

/**
 * **発動**（Issue #567。指示書 `../direction/chain.md`）。**どこで何が発動したか**を 500ms で見せる。**文字は出さない**。
 * 場（モンスター・魔法・罠ゾーン）と場以外（墓地・手札・除外・EX）で分かれる。
 *
 * ```
 *          場のカード                                場以外のカード
 * 0〜100   盤のカードが少し浮き、白い硬い線で縁取られる   出どころ（盤の山（画面の中）→ HUD → 画面の上の外）にカードが現れる
 *          （盤のカードを借りる。縁は外から締まる）       （白い硬い縁つき）
 * 100〜400 浮いて縁取られたまま                          カードが画面の中央寄りへ浮き上がる（少し大きく。絵で「何が」）
 * 400〜500 盤に戻る（縁は細くなって消える）               その場で細く潰れて消える（chained の札へつながる）
 * ```
 *
 * 出どころの規則は「場に出る」と同じ（`../fx/cardSource.js::sourceOf`。盤のゾーン（画面の中）→ HUD の部品 → 画面の上の外）。
 * 場所は**カードの今の場所**（`payload.zone`。無ければ発動したときの場所 `triggerZone`）。場か場以外かは状況（`field` / `elsewhere`。
 * 分岐レイヤー `editorPorts/duelSituations/chaining.js` が決め、カードの表裏 `card` も付ける。Issue #589）。
 * もやもやした光は使わない（縁は硬い線、はじめの輪も硬い線）。
 */
export const D = 500;
export const RISE = 100;
export const DROP = 400;

/** 浮く高さ（カードの幅に対する比） */
const LIFT_K = 0.32;
/** 場以外のカードが浮き上がる先（出どころ → 画面の中央 の何割か）と、そこでの幅 */
const TOWARD = 0.55;
const FLOAT_W = 300;

const v = withDefaults(() => [OBJECT_STYLE]);

/** 浮き（0 → 1 → 0）。帯の時刻 t */
export const liftAt = (t) => (t < 0 || t >= D ? 0 : tween(t, 0, RISE, 'back_out') * (1 - tween(t, DROP, D - 10, 'power2_in')));

/** 盤の上の回し（`ObjectLayer.js::cardTurn` と同じ規則） */
function turnOf(zone, c) {
  if (c?.defense) return Math.PI / 2;
  const kind = parseZoneKey(zone)?.kind;
  if (c && !c.faceUp && (kind === 'magic_trap' || kind === 'field')) return Math.PI;
  return 0;
}

/** 盤のカードの上面の高さ（`drawObjects.js::card` と同じ規則） */
function topOf(zone, c, style) {
  const s = v(style);
  const monster = parseZoneKey(zone)?.kind === 'monster';
  return (monster && c?.faceUp && !c?.defense ? s.attackLift : s.cardLift) + s.cardThickness;
}

/** ゾーンの中心（平面の座標） */
function zoneCenter(field, zone) {
  const q = zone ? field?.zonePlane?.(zone) : null;
  if (!Array.isArray(q) || q.length !== 4) return null;
  return [q.reduce((a, p) => a + p[0], 0) / 4, q.reduce((a, p) => a + p[1], 0) / 4];
}

/** 板を線分 a → b の上に置く（盤の上。`[x, z]`、幅 wd） */
function lay(m, a, b, wd, y) {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  m.position.set((a[0] + b[0]) / 2, y, (a[1] + b[1]) / 2);
  m.rotation.y = -Math.atan2(b[1] - a[1], b[0] - a[0]);
  m.scale.set(Math.max(0.001, len), 1, Math.max(0.001, wd));
}

/** 縁の締まり（外の 1.4 倍から 1.06 倍へ）と太さ（消えるときに細る） */
const edgeScale = (t) => 1.4 - 0.28 * tween(t, 0, RISE, 'expo_out');
const edgeThin = (t) => 1 - seg(t, DROP, D - 20);

/** 発動の組み立て。`field` = 場のカード（盤のカードを借りて浮かせ、白い縁）/ false = 場以外（出どころから中央寄りへ浮いて消える） */
export const chainingSpec = (field) => ({
  label: '発動',
  color: '#ffd21a',
  duration: () => D,
  sounds: () => [{ at: 0, name: 'effect', gain: 0.8 }],

  /*
   * **場のカードは盤のカードを借りて浮かせる**（そのゾーンの板にだけ当てる）。帯の終わりで何も返さない → 盤に戻る
   */
  tweaks: ({ payload, localMs: t, stage }) => {
    if (!field || !payload?.uid || t < 0 || t >= D) return {};
    const id = `card:${payload.zone}`;
    // 浮きは縁の線（演出の層）と同じ値から出す（盤の 1 単位）
    const unit = stage?.field?.unit > 0 ? stage.field.unit : null;
    return {
      [payload.uid]: (h) => {
        if (h.part?.id && h.part.id !== id) return;
        h.reset?.();
        const stack = h.stack ?? h.root;
        stack.position.y += (unit ?? h.part?.size?.w ?? 0) * LIFT_K * liftAt(t);
      },
    };
  },

  layers: [
    {
      id: 'field',
      label: '場のカードの白い縁（浮いている間）',
      space: 'board',
      script: (ctx) => {
        const { THREE } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        const f = ctx.stage?.field;
        const unit = f?.unit > 0 ? f.unit : 0;
        const plane = f?.plane ?? null;
        const center = zoneCenter(f, payload.zone);
        if (!center || !(unit > 0) || !plane) return { root };
        const c = payload.card ?? { faceUp: true };
        const cw = unit;
        const ch = unit / CARD_ASPECT;
        const at = [center[0] - plane.w / 2, center[1] - plane.h / 2];
        const yaw = (Number(f.cardAngle?.(payload.zone)) || 0) + turnOf(payload.zone, c);
        const y0 = topOf(payload.zone, c, ctx.style);
        // 縁（4 本）と、はじめに外へ抜ける細い輪（4 本）
        const bars = [0, 1, 2, 3].map(() => add(root, ctx.kit.onBoard(fill(ctx, '#ffffff', 1, 1)), 40));
        const ring = [0, 1, 2, 3].map(() => add(root, ctx.kit.onBoard(fill(ctx, '#ffffff', 1, 1)), 39));
        const cosY = Math.cos(yaw); const sinY = Math.sin(yaw);
        const rot = ([x, z]) => [at[0] + x * cosY + z * sinY, at[1] - x * sinY + z * cosY];
        const rect = (k) => [[-cw / 2 * k, -ch / 2 * k], [cw / 2 * k, -ch / 2 * k], [cw / 2 * k, ch / 2 * k], [-cw / 2 * k, ch / 2 * k]].map(rot);
        return {
          root,
          update: ({ localMs: t }) => {
            const on = t >= 0 && t < D - 20;
            const y = y0 + cw * LIFT_K * liftAt(t) + 0.4;
            const cs = rect(edgeScale(t));
            const thin = edgeThin(t);
            bars.forEach((m, i) => {
              lay(m, cs[i], cs[(i + 1) % 4], cw * 0.1 * thin, y);
              fade(m, on && thin > 0.02 ? 1 : 0);
            });
            // 輪: 縁が締まる間に外へ抜けて細くなる（0〜180ms）
            const rk = tween(t, 20, 180, 'expo_out');
            const rs = rect(1.12 + 0.7 * rk);
            const rThin = 1 - seg(t, 40, 180);
            ring.forEach((m, i) => {
              lay(m, rs[i], rs[(i + 1) % 4], cw * 0.03 * rThin, y);
              fade(m, t >= 20 && t < 180 && rThin > 0.02 ? 1 : 0);
            });
          },
        };
      },
    },
    {
      id: 'away',
      label: '場以外のカード（出どころから中央寄りへ浮いて消える）',
      space: 'screen',
      script: (ctx) => {
        const { THREE } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        const f = ctx.stage?.field;
        const plane = f?.plane ?? null;
        const at = parseZoneKey(payload?.zone);
        if (!at || !plane || !f?.toStage) return { root };
        const { w: W, h: H } = canvasOf(ctx);
        const player = at.player;
        // 出どころ（場に出ると同じ規則）。画面の上の外へ落ちるときは、その人のモンスターゾーンの真上
        const near = zoneCenter(f, `monster:${player}:3`);
        const end = near ? [near[0] - plane.w / 2, 0, near[1] - plane.h / 2] : [0, 0, 0];
        const src = sourceOf({ ...ctx, payload: { fromZone: payload.zone, prev: { faceUp: true } } }, plane, end);
        const s = f.toStage([src.world[0] + plane.w / 2, src.world[2] + plane.h / 2]) ?? [W / 2, -80];
        // はじめの大きさは盤のカードが画面に写る幅（分からなければ 110px）
        const mq = f.zoneQuad?.(`monster:${player}:3`);
        const w0 = mq ? Math.max(60, Math.max(...mq.map((p) => p[0])) - Math.min(...mq.map((p) => p[0]))) : 110;
        const target = [s[0] + (W / 2 - s[0]) * TOWARD, s[1] + (H * 0.52 - s[1]) * TOWARD];
        const c = payload.card ?? {};
        const g = new THREE.Group();
        add(root, g, 30);
        const face = card(ctx, { code: c.code ?? payload.code ?? null, player, faceUp: (c.code ?? payload.code) != null, w: 1 });
        add(g, face.mesh, 30);
        const bars = [0, 1, 2, 3].map(() => add(g, fill(ctx, '#ffffff', 1, 1), 31));
        g.renderOrder = 30;
        return {
          root,
          update: ({ localMs: t }) => {
            const k = tween(t, 40, DROP - 20, 'expo_out');
            const x = s[0] + (target[0] - s[0]) * k;
            const y = s[1] + (target[1] - s[1]) * k;
            const pop = 0.8 + 0.2 * tween(t, 0, RISE, 'expo_out');
            const cw = (w0 + (FLOAT_W - w0) * k) * pop;
            const chh = cw / CARD_ASPECT;
            // 消える: 細く潰れる（縦が 0 へ）
            const sq = 1 - tween(t, DROP, D - 10, 'power3_in');
            place(g, x, y, { sx: 1, sy: Math.max(0.001, sq) });
            place(face.mesh, 0, 0, { s: cw });
            const lw = Math.max(3, cw * 0.035);
            const e = edgeScale(t) * 0.8 + 0.12;   // 外から締まって、カードのすぐ外で止まる
            const hw = (cw / 2) * e + lw / 2; const hh = (chh / 2) * e + lw / 2;
            place(bars[0], 0, -hh, { sx: hw * 2 + lw, sy: lw });
            place(bars[1], 0, hh, { sx: hw * 2 + lw, sy: lw });
            place(bars[2], -hw, 0, { sx: lw, sy: hh * 2 + lw });
            place(bars[3], hw, 0, { sx: lw, sy: hh * 2 + lw });
            const on = t >= 0 && t < D - 10 && sq > 0.01;
            fade(face.mesh, on ? seg(t, 0, 50) : 0);
            bars.forEach((m) => fade(m, on ? seg(t, 0, 50) : 0));
          },
        };
      },
    },
  ].filter((l) => l.id === (field ? 'field' : 'away')),
});
